import axios from "axios";
import { baseUrl } from "../../../../apis/util";
import { buildRoutePath, resolveFlow, AUTOMATED_FLOW_KEYS } from "./flowRouting";

const SUB_IN_NAME = "Klimakompass import";
const SUB_IN_DESC = "Auto-created for Excel / Klimakompass import";
const SUB_OUT_NAME = "Klimakompass import";
const SUB_OUT_DESC = "Auto-created for Excel / Klimakompass import";
const SUB_WASTE_NAME = "Klimakompass import";
const SUB_WASTE_DESC = "Auto-created for Excel / Klimakompass import";

const FINAL_TREATMENT_CATEGORY = "Product Unsold Not Received";

const TARGET_FLOW = {
  energy_main: "external_power",
  transport_ingoing: "ingoing_transport",
  transport_outgoing: "outgoing_transport",
  industrial_gas: "industrial_gas",
  water_locational: "water_source",
  purchased_supply: "supplies",
  waste_surplus: "waste",
  final_treatment_unsold: "comp_final_treatment_unsold",
};

function authHeaders() {
  return {
    Authorization: "Bearer " + localStorage.getItem("access_token"),
    "Content-Type": "application/json",
  };
}

function toNumber(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function importYearOrUndefined() {
  const y = new URLSearchParams(window.location.search).get("year");
  if (y == null || y === "") return undefined;
  const n = parseInt(y, 10);
  return Number.isFinite(n) ? n : undefined;
}

/** Short stable idempotency key (retries = same key). */
function idempotencyKey(businessId, lineIndex, routePath, qty, unit) {
  const s = `${businessId}|${lineIndex}|${routePath}|${qty}|${unit}`;
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) + h + s.charCodeAt(i);
  }
  return `klimakompasset-${businessId}-${lineIndex}-${(h >>> 0).toString(36)}`;
}

export function mapRowByColumnMapping(rawRow, columnMapping) {
  const mapped = {};
  for (const [col, yfsKey] of Object.entries(columnMapping)) {
    if (!yfsKey || String(yfsKey).startsWith("skip")) continue;
    mapped[yfsKey] = rawRow[col];
  }
  return mapped;
}

function matchEnergyType(options, routePath, description) {
  const blob = `${routePath} ${description || ""}`.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const o of options) {
    const label = String(
      o.dropdown_en || o.dropdown || o.name || o.value || ""
    ).toLowerCase();
    if (!label) continue;
    let score = 0;
    if (blob.includes("district") && label.includes("district")) score += 10;
    if (blob.includes("fjernvarme") && label.includes("fjernvarme")) score += 10;
    if (blob.includes("heat") && /heat|varme|fjern/.test(label)) score += 4;
    if (blob.includes("electric") && label.includes("electric")) score += 8;
    if (blob.includes("natural gas") && label.includes("gas")) score += 6;
    if (blob.includes("steam") && label.includes("steam")) score += 6;
    const words = label.split(/[^a-z0-9æøå]+/i).filter((w) => w.length > 2);
    for (const w of words) {
      if (blob.includes(w)) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      best = o;
    }
  }
  return bestScore >= 3 ? best?.value : null;
}

function matchTransportMeans(options, routePath) {
  const blob = String(routePath || "").toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const o of options) {
    const label = String(
      o.dropdown_name_en || o.dropdown_name || o.name || o.value || ""
    ).toLowerCase();
    if (!label) continue;
    let score = 0;
    if (
      /truck|lastbil|diesel|lorry/.test(blob) &&
      /truck|lastbil|diesel|lorry|vehicle|road/.test(label)
    ) {
      score += 10;
    }
    if (/ship|vessel|sea/.test(blob) && /ship|sea|vessel|marine/.test(label)) {
      score += 10;
    }
    if (/air|fly|flight/.test(blob) && /air|aviation|fly/.test(label)) {
      score += 10;
    }
    if (score > bestScore) {
      bestScore = score;
      best = o;
    }
  }
  if (bestScore >= 5) return best?.value;
  const dieselish = options.find(
    (o) =>
      /truck|lastbil|diesel|road/i.test(
        o.dropdown_name_en || o.dropdown_name || ""
      )
  );
  return dieselish?.value || options[0]?.value;
}

function findDenmarkValue(countryOptions) {
  const d = countryOptions.find(
    (c) =>
      /denmark|danmark/i.test(
        String(c.country_en || c.country || c.name || c.value)
      )
  );
  return d?.value || countryOptions[0]?.value;
}

function gasTypeString(mapped) {
  const g = mapped.gas_type;
  if (g == null) return "Unknown";
  return String(g).split("(")[0].trim() || "Unknown";
}

function matchByKeywords(options, blob, { valueKey = "value" } = {}) {
  const b = blob.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const o of options) {
    const label = String(
      o[valueKey] || o.name || o.dropdown_en || o.dropdown || ""
    ).toLowerCase();
    if (!label) continue;
    let score = 0;
    const words = label.split(/[^a-z0-9]+/i).filter((w) => w.length > 2);
    for (const w of words) {
      if (b.includes(w)) score += 2;
    }
    if (b.includes("combustion") && /combust|inciner|forbrænd/i.test(label)) {
      score += 8;
    }
    if (b.includes("plast") && /plast|plastic/i.test(label)) score += 8;
    if (b.includes("plastic") && /plast|plastic/i.test(label)) score += 8;
    if (score > bestScore) {
      bestScore = score;
      best = o;
    }
  }
  return bestScore >= 2 ? best : null;
}

function matchHiotCategoryId(categories, routePath, description) {
  const blob = `${routePath} ${description || ""}`.toLowerCase();
  if (!Array.isArray(categories) || categories.length === 0) return null;
  let best = null;
  let bestScore = 0;
  for (const c of categories) {
    const label = String(
      c.product_type_name_en || c.product_type_name || ""
    ).toLowerCase();
    if (!label) continue;
    let score = 0;
    if (blob.includes("plastic") && label.includes("plastic")) score += 8;
    if (blob.includes("chemical") && label.includes("chem")) score += 6;
    if (/\bother\b/.test(blob) && /\bother\b/.test(label)) score += 5;
    const words = label.split(/[^a-z0-9]+/i).filter((w) => w.length > 3);
    for (const w of words) {
      if (blob.includes(w)) score += 2;
    }
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return (
    (bestScore >= 4 ? best?.id : null) ||
    categories.find((c) => /other/i.test(c.product_type_name_en || ""))?.id ||
    categories[0]?.id
  );
}

function pickWaterType(waterRows, unit, routePath, description) {
  const blob = `${routePath} ${description || ""}`.toLowerCase();
  const u = String(unit || "").toLowerCase().trim();
  if (!Array.isArray(waterRows) || waterRows.length === 0) return null;
  let candidates = waterRows;
  const unitMatch = waterRows.filter((row) => {
    const pu = String(
      row.physical_unit || row.unit || row.Physical_unit || ""
    )
      .toLowerCase()
      .trim();
    return !pu || pu === u || u.endsWith(pu) || pu.endsWith(u);
  });
  if (unitMatch.length > 0) candidates = unitMatch;

  let best = null;
  let score = 0;
  for (const row of candidates) {
    const nameEn = String(
      row.material_name_en || row.material_name || row.name || ""
    ).toLowerCase();
    let s = 0;
    if (/water|vand|tap|drink|rain|waste.?water|drain/i.test(blob)) {
      if (/water|vand|tap|drink/i.test(nameEn)) s += 6;
    }
    if (blob.includes("clean") && /water|vand/i.test(nameEn)) s += 4;
    const words = nameEn.split(/[^a-z0-9]+/i).filter((w) => w.length > 3);
    for (const w of words) {
      if (blob.includes(w)) s += 1;
    }
    if (row.typedb_water) s += 1;
    if (s > score) {
      score = s;
      best = row;
    }
  }
  if (!best) best = candidates[0];
  const typeWater =
    best.material_name_en || best.material_name || best.value || "Water";
  const typeDBWater =
    best.typedb_water ||
    best.type_db_water ||
    best.type_db ||
    best.typeWaterDb ||
    best.material_code ||
    typeWater;
  return { typeWater, typeDBWater };
}

async function fetchPublic(path) {
  const { data } = await axios.get(baseUrl + path, { headers: {} });
  return data;
}

async function fetchAuth(path) {
  const { data } = await axios.get(baseUrl + path, { headers: authHeaders() });
  return data;
}

async function getPurchasedEnergyOptions() {
  const res = await fetchPublic("/api/business/global-purchased-energy-idemat");
  const list = res?.data || [];
  return list.map((x) => ({
    ...x,
    value: x.dropdown_en || x.dropdown,
    name: x.dropdown_en || x.dropdown,
  }));
}

async function getTransportVehicleOptions() {
  const res = await fetchPublic("/api/business/transport-deliveries-data");
  const list = res?.data || [];
  return list.map((x) => ({
    ...x,
    value: x.dropdown_name_en || x.dropdown_name,
    name: x.dropdown_name_en || x.dropdown_name,
  }));
}

async function getTransportCountryOptions() {
  const res = await fetchPublic("/api/business/country-transport-data");
  const list = res?.data || [];
  return list.map((x) => ({
    ...x,
    value: x.country_en || x.country,
    name: x.country_en || x.country,
  }));
}

async function getWaterSourceRows() {
  const res = await fetchPublic("/api/business/water-source-data");
  return res?.data || [];
}

async function getHiotDenmarkCategories() {
  const res = await fetchAuth("/api/business/hiot/Denmark");
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
}

async function getWasteMaterialRows() {
  const res = await fetchAuth("/api/business/waste-material");
  return res?.data || [];
}

async function getFinalTreatmentStrategies() {
  const res = await fetchPublic("/api/business/comp-final-treatment-dropdown");
  const list = res?.data || [];
  return list.map((x) => ({
    ...x,
    value: x.dropdown_en || x.dropdown,
    name: x.dropdown_en || x.dropdown,
  }));
}

async function ensureIngoingSubcategory(businessId) {
  const { data: subs } = await axios.get(
    baseUrl + "/api/business/" + businessId + "/ingoing-transport-sub",
    { headers: authHeaders() }
  );
  const found = Array.isArray(subs)
    ? subs.find((s) => s.name === SUB_IN_NAME)
    : null;
  if (found) {
    return {
      name: found.name,
      description: found.subcategory_description || SUB_IN_DESC,
    };
  }
  await axios.post(
    baseUrl + "/api/business/" + businessId + "/ingoing-transport-sub",
    {
      name: SUB_IN_NAME,
      subcategory_description: SUB_IN_DESC,
    },
    { headers: authHeaders() }
  );
  return { name: SUB_IN_NAME, description: SUB_IN_DESC };
}

async function ensureOutgoingSubcategory(businessId) {
  const { data: subs } = await axios.get(
    baseUrl + "/api/business/" + businessId + "/outgoing-transport-sub",
    { headers: authHeaders() }
  );
  const found = Array.isArray(subs)
    ? subs.find((s) => s.name === SUB_OUT_NAME)
    : null;
  if (found) {
    return {
      name: found.name,
      description: found.subcategory_description || SUB_OUT_DESC,
    };
  }
  await axios.post(
    baseUrl + "/api/business/" + businessId + "/outgoing-transport-sub",
    {
      name: SUB_OUT_NAME,
      subcategory_description: SUB_OUT_DESC,
    },
    { headers: authHeaders() }
  );
  return { name: SUB_OUT_NAME, description: SUB_OUT_DESC };
}

async function ensureWasteSubcategory(businessId) {
  const { data: subs } = await axios.get(
    baseUrl + "/api/business/" + businessId + "/waste-sub",
    { headers: authHeaders() }
  );
  const found = Array.isArray(subs)
    ? subs.find((s) => s.name === SUB_WASTE_NAME)
    : null;
  if (found) {
    return {
      name: found.name,
      description: found.subcategory_description || SUB_WASTE_DESC,
    };
  }
  await axios.post(
    baseUrl + "/api/business/" + businessId + "/waste-sub",
    {
      name: SUB_WASTE_NAME,
      subcategory_description: SUB_WASTE_DESC,
    },
    { headers: authHeaders() }
  );
  return { name: SUB_WASTE_NAME, description: SUB_WASTE_DESC };
}

function buildWasteMaterialLists(rows) {
  const matKey = "surplus_material_en";
  const dispKey = "current_disposal_en";
  const mats = [
    ...new Set((rows || []).map((r) => r[matKey]).filter(Boolean)),
  ].map((v) => ({ value: v, name: v }));
  const disposals = [
    ...new Set((rows || []).map((r) => r[dispKey]).filter(Boolean)),
  ].map((v) => ({ value: v, name: v }));
  return { mats, disposals };
}

function payloadExternalPower({
  typeMatch,
  qty,
  unit,
}) {
  return {
    type: typeMatch,
    source: null,
    category: "main",
    qty,
    unit,
    single_source: "No",
    price_invoice: null,
    invoice_date: null,
    invoice_number: null,
    qty_consumed: null,
    qty_sold: null,
    qty_wasted: null,
    energy_wasted: null,
  };
}

function payloadIndustrialGas({ gas_type, qty }) {
  return {
    gas_type: String(gas_type).trim(),
    qty,
    price_invoice: null,
    invoice_date: null,
    invoice_number: null,
  };
}

function payloadIngoingTransport({
  supplier,
  qty,
  means,
  denmark,
  total_weight_delivery,
  subIn,
  tco2e,
}) {
  return {
    supplier,
    transport_distance: qty,
    means_transportation: means,
    country_departure: denmark,
    total_weight_delivery,
    packaging_data: null,
    subcategory: subIn.name,
    subcategory_description: subIn.description,
    price_invoice: null,
    invoice_date: null,
    invoice_number: null,
    manuel_distribution: tco2e != null ? tco2e * 1000 : null,
    manuel: null,
  };
}

function payloadOutgoingTransport({
  client,
  qty,
  means,
  denmark,
  total_weight_delivery,
  subOut,
  tco2e,
}) {
  return {
    client,
    transport_distance: qty,
    means_transportation: means,
    country_arrival: denmark,
    total_weight_delivery,
    subcategory: subOut.name,
    subcategory_description: subOut.description,
    price_invoice: null,
    invoice_date: null,
    invoice_number: null,
    manuel: null,
    manuel_distribution: tco2e != null ? tco2e * 1000 : null,
  };
}

function payloadWaterSource({
  qty,
  wt,
  tco2e,
  nickname,
}) {
  const boolSubstitute =
    "No";
  return {
    water_price: null,
    qty,
    category: "found",
    carbon: null,
    subcategory: "non_equipment_locational",
    invoice_date: null,
    invoice_number: null,
    type_water: wt.typeWater,
    type_equipment: null,
    typedb_water: wt.typeDBWater,
    typedb_equipment: null,
    price_invoice: null,
    materials: null,
    weight: null,
    manuel: tco2e != null ? tco2e : null,
    isSubstitute: boolSubstitute,
    nickname,
    production_country: null,
    type_drinking: null,
    typedb_drinking: null,
  };
}

function payloadSupplies({
  name,
  catId,
  qty,
  unit,
}) {
  return {
    name,
    origin: "Denmark",
    category: catId,
    amount: qty,
    unit,
    transport_mean: null,
    weight: null,
    department: null,
    pcs: null,
    lease_or_purchase: null,
    location: "",
    used_for: null,
    transport_cost: null,
  };
}

function payloadWaste({
  mat,
  disp,
  qty,
  subWaste,
  description,
  nickname,
  tco2e,
}) {
  const urlParams = new URLSearchParams(window.location.search);
  const year = urlParams.get("year");
  return {
    type_surplus_material: mat.value,
    type_current_disposal: disp.value,
    is_reused: false,
    category: "surplus materials",
    subcategory: subWaste.name,
    subcategory_description: subWaste.description,
    amount: qty,
    price_invoice: null,
    current_disposal: 0,
    avoided_emission: null,
    product: description,
    surplus_materials_sold: null,
    buyer: null,
    share: null,
    invoice_date: null,
    invoice_number: null,
    type_waste_water: null,
    total_waste_water: null,
    water_sold: null,
    type_surplus_energy: null,
    typedb_surplus_energy: null,
    wasted_surplus_energy: null,
    sold_surplus_energy: null,
    wasted_energy: null,
    money_earned: null,
    wasted_emission: tco2e != null ? tco2e : null,
    nickname,
    year,
  };
}

function payloadFinalTreatmentUnsold({
  product_name,
  denmark,
  end_of_life_strategy,
  qty,
}) {
  return {
    product_name,
    treatment_country: denmark,
    end_of_life_strategy,
    total_weight_treated: qty,
    category: FINAL_TREATMENT_CATEGORY,
    price_invoice: null,
    is_disassembly: false,
    is_leased_take_back: false,
    invoice_date: null,
    invoice_number: null,
  };
}

function normalizeBulkErrors(errors) {
  if (!Array.isArray(errors)) return [];
  return errors.map((e) =>
    typeof e === "object" && e !== null
      ? {
          route: e.line_index != null ? `line ${e.line_index}` : "",
          reason:
            e.detail ||
            e.message ||
            JSON.stringify(e.non_field_errors || e) ||
            String(e),
        }
      : { route: "", reason: String(e) }
  );
}

/**
 * @param {{ businessId: string, rawRows: Record<string, unknown>[], columnMapping: Record<string, string> }} param0
 */
export async function applyYfsImport({ businessId, rawRows, columnMapping }) {
  const result = {
    imported: 0,
    skipped: 0,
    failed: 0,
    errors: [],
  };

  const [
    energyOptions,
    vehicleOptions,
    countryOptions,
    subIn,
    subOut,
    subWaste,
    waterRows,
    hiotCategories,
    wasteRows,
    finalStrategies,
  ] = await Promise.all([
    getPurchasedEnergyOptions(),
    getTransportVehicleOptions(),
    getTransportCountryOptions(),
    ensureIngoingSubcategory(businessId),
    ensureOutgoingSubcategory(businessId),
    ensureWasteSubcategory(businessId),
    getWaterSourceRows(),
    getHiotDenmarkCategories(),
    getWasteMaterialRows(),
    getFinalTreatmentStrategies(),
  ]);

  const denmark = findDenmarkValue(countryOptions);
  const { mats: wasteMaterials, disposals: wasteDisposals } =
    buildWasteMaterialLists(wasteRows);

  /** @type {{ target_flow: string, payload: Record<string, unknown>, line_index: number, idempotency_key: string }[]} */
  const lines = [];
  let clientSkipped = 0;

  for (let rowIndex = 0; rowIndex < rawRows.length; rowIndex += 1) {
    const raw = rawRows[rowIndex];
    const mapped = mapRowByColumnMapping(raw, columnMapping);
    const routePath = buildRoutePath(mapped);
    const flow = resolveFlow(mapped);
    const qty = toNumber(mapped.quantity);
    const unit = mapped.unit != null ? String(mapped.unit).trim() : "";
    const tco2e = toNumber(mapped.tco2e);

    if (qty == null || !unit) {
      clientSkipped += 1;
      continue;
    }

    if (!AUTOMATED_FLOW_KEYS.has(flow.key)) {
      clientSkipped += 1;
      continue;
    }

    const target_flow = TARGET_FLOW[flow.key];
    if (!target_flow) {
      clientSkipped += 1;
      continue;
    }

    const idempotency_key = idempotencyKey(
      businessId,
      rowIndex,
      routePath,
      qty,
      unit
    );

    if (flow.key === "energy_main") {
      const typeMatch = matchEnergyType(
        energyOptions,
        routePath,
        mapped.description
      );
      if (!typeMatch) {
        clientSkipped += 1;
        result.errors.push({
          route: routePath,
          reason: "No matching purchased energy type in YFS list",
        });
        continue;
      }
      lines.push({
        target_flow,
        payload: payloadExternalPower({ typeMatch, qty, unit }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    if (flow.key === "industrial_gas") {
      lines.push({
        target_flow,
        payload: payloadIndustrialGas({
          gas_type: gasTypeString(mapped),
          qty,
        }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    if (flow.key === "transport_ingoing") {
      const means = matchTransportMeans(vehicleOptions, routePath);
      const u = unit.toLowerCase();
      const isTonKm = /ton\.?\s*km|tkm/i.test(u);
      lines.push({
        target_flow,
        payload: payloadIngoingTransport({
          supplier: mapped.supplier_client || "Import",
          qty,
          means,
          denmark,
          total_weight_delivery: isTonKm ? 1 : qty,
          subIn,
          tco2e,
        }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    if (flow.key === "transport_outgoing") {
      const means = matchTransportMeans(vehicleOptions, routePath);
      const u = unit.toLowerCase();
      const isTonKm = /ton\.?\s*km|tkm/i.test(u);
      lines.push({
        target_flow,
        payload: payloadOutgoingTransport({
          client: mapped.supplier_client || "Import",
          qty,
          means,
          denmark,
          total_weight_delivery: isTonKm ? 1 : qty,
          subOut,
          tco2e,
        }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    if (flow.key === "water_locational") {
      const wt = pickWaterType(waterRows, unit, routePath, mapped.description);
      if (!wt) {
        clientSkipped += 1;
        result.errors.push({
          route: routePath,
          reason: "Water source catalogue empty or unavailable",
        });
        continue;
      }
      lines.push({
        target_flow,
        payload: payloadWaterSource({
          qty,
          wt,
          tco2e,
          nickname: String(mapped.description || "").slice(0, 120) || null,
        }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    if (flow.key === "purchased_supply") {
      const catId = matchHiotCategoryId(
        hiotCategories,
        routePath,
        mapped.description
      );
      if (!catId) {
        clientSkipped += 1;
        result.errors.push({
          route: routePath,
          reason: "No HIoT product category (Denmark) available",
        });
        continue;
      }
      lines.push({
        target_flow,
        payload: payloadSupplies({
          name: String(mapped.description || "Imported purchase").slice(
            0,
            200
          ),
          catId,
          qty,
          unit,
        }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    if (flow.key === "waste_surplus") {
      const blob = `${routePath} ${mapped.description || ""}`;
      const mat =
        matchByKeywords(wasteMaterials, blob) ||
        wasteMaterials.find((m) => /plast|plastic/i.test(m.value));
      const disp =
        matchByKeywords(wasteDisposals, blob) ||
        wasteDisposals.find((d) =>
          /combust|inciner|forbrænd/i.test(d.value)
        );
      if (!mat || !disp) {
        clientSkipped += 1;
        result.errors.push({
          route: routePath,
          reason: "Could not match waste material / disposal dropdowns",
        });
        continue;
      }
      lines.push({
        target_flow,
        payload: payloadWaste({
          mat,
          disp,
          qty,
          subWaste,
          description:
            String(mapped.description || "").slice(0, 200) || null,
          nickname: String(mapped.description || "").slice(0, 120) || null,
          tco2e,
        }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    if (flow.key === "final_treatment_unsold") {
      const strat =
        matchByKeywords(finalStrategies, routePath, {
          valueKey: "value",
        }) ||
        finalStrategies.find((s) =>
          /combust|inciner|forbrænd|landfill|genbrug/i.test(
            String(s.value || s.dropdown_en || "")
          )
        );
      if (!strat) {
        clientSkipped += 1;
        result.errors.push({
          route: routePath,
          reason: "Could not match end-of-life strategy dropdown",
        });
        continue;
      }
      const endStrategy =
        strat.id != null && strat.id !== ""
          ? strat.id
          : strat.value || strat.dropdown_en;
      lines.push({
        target_flow,
        payload: payloadFinalTreatmentUnsold({
          product_name: String(mapped.description || "Imported product").slice(
            0,
            200
          ),
          denmark,
          end_of_life_strategy: endStrategy,
          qty,
        }),
        line_index: rowIndex,
        idempotency_key,
      });
      continue;
    }

    clientSkipped += 1;
  }

  result.skipped = clientSkipped;

  if (lines.length === 0) {
    return result;
  }

  const year = importYearOrUndefined();

  try {
    const { data } = await axios.post(
      `${baseUrl}/api/business/${businessId}/import/ghg-lines`,
      {
        source: "klimakompasset",
        ...(year !== undefined ? { year } : {}),
        lines,
      },
      { headers: authHeaders() }
    );

    result.imported = Array.isArray(data?.created) ? data.created.length : 0;
    result.skipped =
      clientSkipped + (Array.isArray(data?.skipped) ? data.skipped.length : 0);
    result.failed = Array.isArray(data?.errors) ? data.errors.length : 0;
    result.errors = [
      ...result.errors,
      ...normalizeBulkErrors(data?.errors),
    ];

    if (data?.message && data.message !== "ok") {
      result.errors.push({ route: "", reason: String(data.message) });
    }
  } catch (e) {
    const msg =
      e?.response?.data != null
        ? typeof e.response.data === "object"
          ? JSON.stringify(e.response.data)
          : String(e.response.data)
        : e?.message || String(e);
    result.failed = lines.length;
    result.errors.push({
      route: "bulk import",
      reason: msg,
    });
  }

  return result;
}
